import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { StickyNote } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import ConfirmActionModal from "@/components/shared/ConfirmActionModal";
import { useAuth } from "@/hooks/useAuth";
import useLiveResource from "@/hooks/useLiveResource";
import { PERMISSIONS } from "@/permissions/constant";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { apiErrorMessageFormat, serverFieldErrors } from "@/utils/formErrorUtils";
import { CandidateNoteService } from "@/services/candidateNoteService";

import NoteRow from "./component/NoteRow";
import NoteComposer from "./component/NoteComposer";

const PER_PAGE = 10;
/** The route's own ceiling — `per_page: int = Query(..., le=100)`. */
const MAX_PER_PAGE = 100;

const SNIPPET_LENGTH = 60;

/** The opening words of a note, so a delete question names what it destroys. */
const snippet = (body = "") => (body.length > SNIPPET_LENGTH ? `${body.slice(0, SNIPPET_LENGTH).trimEnd()}…` : body);

/** Three rows of the shape the real ones take, so the panel does not jump when they arrive. */
const StreamSkeleton = () => (
  <div role="status" aria-label="Loading notes" className="space-y-1">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex animate-pulse gap-3 rounded-xl px-3 py-3">
        <div className="mt-0.5 h-9 w-9 shrink-0 rounded-full bg-background-tertiary" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3.5 w-40 rounded bg-background-tertiary" />
          <div className="h-3.5 w-full rounded bg-background-tertiary" />
          <div className="h-3.5 w-4/6 rounded bg-background-tertiary" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * The Notes tab on a candidate.
 *
 * It fetches its own data: `candidate_detail_by_id` carries no `candidate_notes`
 * key, deliberately — there is no count badge on the tab, so nothing about
 * notes belongs in that payload.
 *
 * **One stream, two scopes.** With a job on screen the tab shows this
 * candidate's own notes — the ones that follow the person onto every
 * application — plus the notes filed against this job, and nothing from a
 * sibling application. The server does that split in
 * `candidate_note/repository.py::_scope_to_view` (§1).
 *
 * Which of the two a note has is said in words under it, in the same words the
 * composer offered when it was written, so a reader never has to infer it and a
 * writer can see afterwards what they chose. Getting it wrong is recoverable:
 * the scope is editable in place, the same way the body is.
 *
 * Every control the server would refuse is absent rather than disabled, and the
 * rules come from §3:
 *
 * - compose — `note.manage`, and not a SuperAdmin (SUPERADMIN_CANNOT_WRITE_NOTE)
 * - edit    — `note.manage`, and your own note. Body and scope both: the
 *             scope is part of what the author said, and correcting it is
 *             theirs to do
 * - delete  — `note.manage`, and your own note. No admin exception: a note
 *             carries its author's name on a hiring record, and removing their
 *             words is not another seat's to do
 *
 * Nothing here reads a role. `note.manage` plus authorship answers all three.
 *
 * The `note_added` socket event is an accelerator only. This tab is correct with
 * no event ever arriving: it fetches on mount and after every write of its own.
 */
const Notes = ({ candidateId, applicationId = null, candidateDetails }) => {
  const { user } = useAuth();
  const permissions = getUserPermissions(user);
  const canManage = hasPermission(PERMISSIONS.NOTE_MANAGE, permissions);
  const canWrite = canManage && !user?.isSuperAdmin;

  const [items, setItems] = useState([]);
  // How many pages of the stream are on screen. A refresh reloads exactly this
  // window in one request, rather than dropping the reader back to page 1 —
  // deleting a note on page 3 used to collapse the list under them.
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [busyNoteId, setBusyNoteId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The newest request wins. A page that lands after a newer one went out would
  // overwrite fresher rows with staler ones.
  const listRequestRef = useRef(0);
  // The same number as `pagesLoaded`, readable inside `reload` without making
  // `reload` depend on it — a reload scheduled by a write must not need
  // re-creating every time a page is added. Written where the state is written,
  // never during render: a render React throws away must not move it.
  const pagesLoadedRef = useRef(1);

  /**
   * Load the top `pages` pages of the stream as one read, replacing what is on
   * screen, rather than appending only the new page. Appending across offsets
   * double-counts: a note written between two pages shifts every later row
   * down one, so a row arrives twice and the row it displaced never arrives
   * at all.
   *
   * The route caps `perPage` at `MAX_PER_PAGE`, so a window wider than that is
   * split into successive `MAX_PER_PAGE` requests, fired as one burst and
   * concatenated in order, then trimmed to exactly `pages * PER_PAGE` rows.
   * `hasNext` follows the concatenated list once it is longer than the window,
   * and the last request's own answer otherwise — so Load more keeps working
   * past the 100th note instead of re-fetching the same full window forever.
   */
  const load = useCallback(
    async (pages, { silent = false, more = false } = {}) => {
      // No candidate is not "still loading". Returning here with `loading` left
      // true is a spinner that never resolves, on a screen with nothing coming.
      if (!candidateId) {
        setLoading(false);
        setError("We couldn't tell which candidate these notes belong to. Reopen the candidate from the list.");
        return;
      }

      const requestId = listRequestRef.current + 1;
      listRequestRef.current = requestId;

      const windowSize = PER_PAGE * pages;
      const pageCount = Math.ceil(windowSize / MAX_PER_PAGE);

      if (!silent) {
        if (more) setLoadingMore(true);
        else setLoading(true);
      }

      try {
        const responses = await Promise.all(
          Array.from({ length: pageCount }, (_, index) =>
            CandidateNoteService.list(candidateId, { applicationId, page: index + 1, perPage: MAX_PER_PAGE }),
          ),
        );

        if (!mountedRef.current || listRequestRef.current !== requestId) return;

        const combined = responses.flatMap((response) => response?.data?.items ?? []);
        const lastResponse = responses[responses.length - 1];

        setItems(combined.slice(0, windowSize));
        setHasNext(combined.length > windowSize || Boolean(lastResponse?.pagination?.hasNext));
        setPagesLoaded(pages);
        pagesLoadedRef.current = pages;
        setError(null);
      } catch (err) {
        if (!mountedRef.current || listRequestRef.current !== requestId) return;

        const message = apiErrorMessageFormat(err, "We couldn't load these notes.");
        // A failed background refresh must not wipe what is already on screen.
        if (!silent) {
          if (!more) setError(message);
          toast.error(message);
        }
      } finally {
        if (mountedRef.current && listRequestRef.current === requestId && !silent) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [candidateId, applicationId],
  );

  useEffect(() => {
    load(1);
  }, [load]);

  /** Re-read the window already on screen, without a spinner. */
  const reload = useCallback(() => load(pagesLoadedRef.current, { silent: true }), [load]);

  // Every note event carries `parent.candidate`, and the stream on screen is
  // this candidate's. A colleague writing about someone else must not move it.
  useLiveResource({
    keys: "note",
    enabled: Boolean(candidateId),
    match: (event) => String(event.parent?.candidate) === String(candidateId),
    onChange: reload,
  });

  /**
   * One place for what a failed write means.
   *
   * `NOTE_NOT_FOUND` is the only code that needs more than a sentence: somebody
   * deleted the note first, so the ghost row has to leave the screen. The others
   * already carry a message written for the reader to act on.
   *
   * A failure that names a form field is left to the form. The composer puts it
   * on the field and takes the focus, and a toast saying the same sentence twice
   * is noise.
   */
  const reportWriteFailure = (err, fallback) => {
    if (err?.response?.data?.error_code === "NOTE_NOT_FOUND") {
      toast.error("That note has already been deleted.");
      reload();
      return;
    }
    if (Object.keys(serverFieldErrors(err)).length) return;
    toast.error(apiErrorMessageFormat(err, fallback));
  };

  /**
   * The composer resolves the scope to an id: this screen's application, or
   * null for a note about the person. The tab passes it straight through.
   */
  const handleCreate = async ({ applicationId: noteApplicationId, body }) => {
    try {
      await CandidateNoteService.create({ candidateId, applicationId: noteApplicationId, body });
      toast.success("Note added.");
      await reload();
    } catch (err) {
      reportWriteFailure(err, "We couldn't add that note.");
      // Rethrown so the composer keeps the words on screen.
      throw err;
    }
  };

  /**
   * An edit applies the row the server returned, in place — neither a reworded
   * note nor a rescoped one moves in a list ordered by `created_at`, and
   * refetching would cost the reader every page past the first.
   *
   * That holds for the scope too, because both scopes are on screen together:
   * widening a note to the candidate or narrowing it to this job leaves it
   * inside `_scope_to_view` either way, so no edit can strand a row in a view
   * that no longer contains it.
   *
   * It rethrows, and that is what keeps the editor open on the reworded text
   * when the save fails. Swallowing the error closed the editor and lost the
   * words, leaving a toast about a note nobody could retype.
   */
  const handleEdit = async (note, changes) => {
    setBusyNoteId(note.id);
    try {
      const response = await CandidateNoteService.update(note.id, changes);
      const updated = response?.data;
      if (!mountedRef.current) return;
      if (updated) setItems((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success("Note updated.");
    } catch (err) {
      reportWriteFailure(err, "We couldn't save that note.");
      throw err;
    } finally {
      if (mountedRef.current) setBusyNoteId(null);
    }
  };

  const handleDelete = (note) =>
    setConfirmation({
      title: "Delete your note?",
      lines: [`"${snippet(note.body)}"`, "This deletes the note for everyone who can see it, and cannot be undone. The Activity tab keeps a line saying it was deleted."],
      actionText: "Delete note",
      onConfirm: async () => {
        setBusyNoteId(note.id);
        try {
          await CandidateNoteService.remove(note.id);
          if (!mountedRef.current) return;
          setItems((rows) => rows.filter((row) => row.id !== note.id));
          toast.success("Note deleted.");
        } catch (err) {
          reportWriteFailure(err, "We couldn't delete that note.");
        } finally {
          if (mountedRef.current) setBusyNoteId(null);
        }
      },
    });

  // Authorship is the whole of edit and delete. `String` on both sides because
  // one is a UUID off the auth payload and the other off a note row, and a
  // strict compare between a string and anything else silently denies the
  // author their own note.
  const isMine = (note) => Boolean(user?.id) && String(note.author?.id) === String(user.id);

  // One draft per candidate per application. The same person on two jobs keeps
  // two half-written notes, which is the only reading of it that is not a
  // surprise.
  const draftKey = candidateId ? `note:${candidateId}:${applicationId ?? "candidate"}` : "";

  return (
    <div className="flex h-full min-h-0 flex-col bg-background-secondary">
      {canWrite && (
        // **Its own ground, not just its own space.** Lifted into a card with
        // its own border and shadow rather than just a hairline under a shared
        // background — on one flat background with a rule, the scope choice
        // sat close enough to the stream below that a reader took it for a
        // filter over the notes. A distinct card makes writing and reading two
        // places rather than two halves of one.
        //
        // Held above the stream so the box is never scrolled away from: writing
        // about something you just read should not mean scrolling back up past
        // it. Keyed on the draft, so moving to another candidate or another
        // application mounts a fresh composer holding that one's draft rather
        // than the previous screen's words.
        <div className="shrink-0 px-4 pb-2 pt-4">
          {/* Same background as the panel around it — no colour block sitting
              on the page. The border and shadow are what say "surface", and
              both step up on hover/focus so the lift itself reads as the
              invitation to write, rather than a permanent tinted box. */}
          <div className="rounded-2xl border border-border-primary bg-background-secondary p-4 shadow-sm transition-shadow duration-150 hover:shadow-md focus-within:shadow-md focus-within:border-text-secondary/40">
            <NoteComposer
              key={draftKey}
              applicationId={applicationId}
              jobTitle={candidateDetails?.job_title}
              candidateName={candidateDetails?.candidate_name}
              draftKey={draftKey}
              onSubmit={handleCreate}
            />
          </div>
        </div>
      )}

      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1">
        {loading ? (
          <StreamSkeleton />
        ) : error ? (
          // A solid border, not dashed — this is a fault, not an invitation.
          // No fill: it sits on the panel's own background like everything
          // else here, the border alone marks it off as its own region.
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-border-primary py-12 text-center">
            <p className="max-w-[360px] text-sm text-text-primary">{error}</p>
            {candidateId && (
              <ButtonShadcn type="button" size="sm" variant="secondary" onClick={() => load(1)}>
                Try again
              </ButtonShadcn>
            )}
          </div>
        ) : items.length === 0 ? (
          // Dashed rather than solid — an empty slot waiting to be filled,
          // not a boxed-off block. Still no fill, for the same reason.
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border-primary py-12 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-tertiary">
              <StickyNote className="h-5 w-5 text-text-muted" aria-hidden="true" />
            </span>
            <p className="text-sm font-medium text-text-primary">No notes yet</p>
            <p className="max-w-[340px] text-xs leading-relaxed text-text-secondary">
              {canWrite
                ? "Whatever you learn about this candidate goes here — the next person to open them reads it first. Keep it to this job, or let it follow them onto every job they apply to."
                : "Nobody has written about this candidate yet."}
            </p>
          </div>
        ) : (
          // No card around the list itself — it's meant to read as more of
          // the page, not a panel on top of it. The hairlines between rows
          // and each row's own hover highlight are what give it structure;
          // nothing here needs a border of its own.
          <section aria-label="Notes on this candidate">
            <div className="divide-y divide-border-primary/70">
              {items.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  applicationId={applicationId}
                  canEdit={canWrite && isMine(note)}
                  canDelete={canWrite && isMine(note)}
                  busy={busyNoteId === note.id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {hasNext && (
              <div className="flex justify-center pt-3">
                <ButtonShadcn type="button" variant="secondary" size="lg" disabled={loadingMore} onClick={() => load(pagesLoaded + 1, { more: true })}>
                  {loadingMore ? "Loading…" : "Load more"}
                </ButtonShadcn>
              </div>
            )}
          </section>
        )}
      </div>

      <ConfirmActionModal confirmation={confirmation} onClose={() => setConfirmation(null)} />
    </div>
  );
};

export default Notes;